import React from 'react';
import PropTypes from 'prop-types';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Table, Space, Input, Button } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import GlobalLoading from 'components/app/components/common/globalLoading/globalLoading';

import './createdTable.scss';
import NoDataComponent from '../../../../../../../../ComponentsV2/CommonComponents/MobileView/noDataComponent/NoDataComponent';

const CreatedTable = ({
    data,
    onRefreshData,
    pagination,
    loading,
    search,
    onClickDelete,
    onClickPreview,
    searchValue,
    setSearchValue,
}) => {
    const { projectId, companyId } = useParams();
    const history = useHistory();

    const { activeTimezone: timezone } = useSelector(
        (state) => state?.settings
    );

    //Search part
    let searchInput;
    const getColumnSearchProps = (filterKey) => ({
        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
        }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={(node) => {
                        searchInput = node;
                    }}
                    placeholder={`Search`}
                    value={selectedKeys[0]}
                    defaultValue={searchValue}
                    onChange={(e) => {
                        setSearchValue(e.target.value);
                        setSelectedKeys(e.target.value ? [e.target.value] : []);
                    }}
                    onPressEnter={() => handleSearch(selectedKeys, confirm)}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{ color: filtered ? '#1890ff' : undefined }}
            />
        ),
        onFilter: (value, record) => {
            if (filterKey === 'exhibitor') {
                const exName = record.exhibitorCompanyProfile?.name;
                return exName
                    ? exName
                          .toString()
                          .toLowerCase()
                          .includes(value.toLowerCase())
                    : false;
            }
            return record[filterKey]
                ? record[filterKey]
                      .toString()
                      .toLowerCase()
                      .includes(value.toLowerCase())
                : '';
        },
        onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput?.select(), 100);
            }
        },
        render: (text) => text,
    });

    const handleSearch = (selectedKeys, confirm) => {
        let searchTextSelect = selectedKeys[0]
            ? selectedKeys[0]
            : searchValue;
        if (!/\s/.test(searchTextSelect)) {
            confirm();
            let trimSearchText = searchTextSelect?.trim();
            search(trimSearchText);
        }
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchValue('');
        search('');
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            sorter: (a, b) => a?.name.localeCompare(b?.name),
            ...getColumnSearchProps('name'),
        },
        {
            title: 'Sponsor',
            dataIndex: 'exhibitorCompanyProfile',
            sorter: (a, b) => a?.name.localeCompare(b?.name),
            ...getColumnSearchProps('exhibitor'),
            render: (exhibitorCompanyProfile, item) => {
                if (isEmpty(exhibitorCompanyProfile)) return null;
                return (
                    <span className="custom-cut-sponsor-name">
                        {exhibitorCompanyProfile?.name}
                    </span>
                );
            },
        },
        {
            title: 'Tags',
            dataIndex: 'topicTags',
            sorter: (a, b) => a?.topicTags?.length - b?.topicTags?.length,
            render: (topicTags, item) => {
                if (isEmpty(topicTags)) return null;
                return topicTags.map((tag, i) => {
                    return (
                        <span
                            key={'tags-key-' + i}
                            className="badge badge-xpmanager custom-cut-badge tw-py-1.5 tw-px-4.5 border-solid border border-border-gray-light md:tw-border-none"
                        >
                            {tag?.name}
                        </span>
                    );
                });
            },
        },
        {
            title: 'Last edited on',
            dataIndex: 'updatedAt',
            sorter: (a, b) =>
                moment(a.updatedAt).unix() - moment(b.updatedAt).unix(),
            render: (updatedAt, item) => {
                if (!updatedAt) return null;
                return (
                    <span>
                        {moment(updatedAt)
                            .tz(timezone)
                            .format('DD MMMM YYYY [at] HH:mm')}
                    </span>
                );
            },
        },
        {
            title: () => (
                <span className="tw-flex tw-justify-center md:tw-hidden">
                    Actions
                </span>
            ),
            render: (item) => {
                return (
                    <div className="d-flex justify-content-end">
                        <button
                            className="myxp btn btn-sm btn-link"
                            onClick={() => onClickPreview(item)}
                        >
                            Preview
                        </button>
                        <button
                            className="myxp btn btn-sm btn-link"
                            onClick={() =>
                                history.push({
                                    pathname: `/company/${companyId}/project/${projectId}/products/edit-product/${item.id}`,
                                })
                            }
                        >
                            Edit
                        </button>
                        <button
                            className="myxp btn btn-sm btn-link"
                            onClick={() => onClickDelete(item)}
                        >
                            Delete
                        </button>
                    </div>
                );
            },
        },
    ];

    const globalLoadingComponent = {
        spinning: loading,
        indicator: <GlobalLoading />,
    };

    const renderPagination = (current, type, originalElement) => {
        return (
            <a
                onClick={() => {
                    if (originalElement?.type === 'button') {
                        if (data?.pagination?.currentPage === 1) {
                            return null;
                        }
                    }
                    pagination(current);
                }}
            >
                {originalElement}
            </a>
        );
    };

    return (
        <>
            {!loading && data?.products?.length ? (
                <Table
                    className="sponsors-company-table"
                    rowKey="id"
                    columns={columns}
                    dataSource={data?.products}
                    loading={globalLoadingComponent}
                    pagination={
                        data?.pagination?.totalPage > 1
                            ? {
                                  itemRender: renderPagination,
                                  pageSize: 10,
                                  total: data?.pagination?.total,
                                  current: data?.pagination?.currentPage,
                              }
                            : false
                    }
                    style={{ overflow: 'auto' }}
                />
            ) : (
                <NoDataComponent
                    loading={loading}
                    buttonText={'Create Product'}
                    description={'No products have been Created'}
                    onClickHandler={() =>
                        history.push(
                            `/company/${companyId}/project/${projectId}/products/create-product`
                        )
                    }
                />
            )}
            <style>{`
            .sponsors-company-table .ant-table{
                overflow: auto !important;
            }
            .sponsors-company-table .ant-table-container table > thead > tr:first-child th:first-child
            {
                padding-left: 0px;
            }
            `}</style>
        </>
    );
};

CreatedTable.propTypes = {
    data: PropTypes.object,
    onRefreshData: PropTypes.func,
    pagination: PropTypes.func,
    search: PropTypes.func,
    loading: PropTypes.bool,
    onClickPreview: PropTypes.func,
    onClickDelete: PropTypes.func,
    searchValue: PropTypes.string,
    setSearchValue: PropTypes.func,
};

CreatedTable.defaultProps = {
    data: null,
    onRefreshData: () => {},
    pagination: () => {},
    search: () => {},
    loading: false,
    onClickPreview: () => {},
    onClickDelete: () => {},
};

export default CreatedTable;
